import { toast } from 'react-toastify'
import { useEffect, useState } from 'react'
import { AutomationModal, AutomationFormData } from './AutomationModal'
import {
  AutomationCreatePayload,
  AutomationDetail,
  AutomationMultipartPayload,
} from 'models/automation'
import apiService from 'services/api'
import {
  cleanAutomationActionPayload,
  cleanAutomationConditionPayload,
} from './utils'

interface EditAutomationModalProps {
  show: boolean
  handleClose: () => void
  onAutomationUpdated?: () => void
  automationId: number
}

export function EditAutomationModal({
  show,
  handleClose,
  onAutomationUpdated,
  automationId,
}: EditAutomationModalProps) {
  const [automation, setAutomation] = useState<AutomationDetail | null>(null)
  const [isLoadingAutomation, setIsLoadingAutomation] = useState(false)

  const api = apiService()

  const fetchAutomationDetails = async () => {
    if (!show || !automationId) return

    try {
      setIsLoadingAutomation(true)
      const response = await api.getAutomationById(automationId)
      setAutomation(response)
    } catch (err) {
      console.error('Failed to fetch automation details:', err)
      toast.error('Failed to load automation details')
      handleClose()
    } finally {
      setIsLoadingAutomation(false)
    }
  }

  useEffect(() => {
    if (show && automationId) {
      fetchAutomationDetails()
    }
  }, [show, automationId])

  const handleSubmitSuccess = () => {
    toast.success('Automation updated successfully')
    onAutomationUpdated?.()
  }

  const updateAutomation = async (values: AutomationFormData) => {
    if (!automation) return

    // Transform form data to match database structure for update
    const automationData: AutomationCreatePayload = {
      name: values.name,
      description: values.description,
      is_active: true,
      // Update condition group with single condition
      condition_groups: [
        {
          logical_operator: 'OR',
          order_index: 0,
          conditions: [cleanAutomationConditionPayload(values, 0)],
        },
      ],
      // Update action
      actions: [cleanAutomationActionPayload(values)],
    }

    if (values.action_type === 'add_company_note') {
      // Create multipart payload for company note automation
      const multipartData: AutomationMultipartPayload = {
        name: values.name,
        description: values.description,
        is_active: true,
        condition_groups: automationData.condition_groups,
        actions: automationData.actions,
        attachments: values.attachments || [],
      }
      return api.updateCompanyNoteAutomation(automation.id, multipartData)
    }

    return api.updateAutomation(automation.id, automationData)
  }

  return (
    <AutomationModal
      show={show}
      handleClose={handleClose}
      onSubmitSuccess={handleSubmitSuccess}
      confirmButtonText="Update"
      submitApiAction={updateAutomation}
      automationToEdit={automation!}
      title="Edit Automation"
      description="Update your automation settings"
      loading={isLoadingAutomation}
    />
  )
}
